<template>
  <div>
    <SDialog v-model:open="modalVisibility">
      <SDialogContent
        class="promocode-campaigns-modal !w-[640px] !max-w-[640px] !max-h-[85vh] !p-0 !gap-0 !grid-rows-[auto_1fr]"
        @open-auto-focus.prevent
      >
        <SDialogHeader
          class="space-y-4 border-b border-[#e8e8e8] px-6 pb-5 pt-6 text-left"
        >
          <SDialogTitle class="text-xl font-semibold text-[#202223]">
            {{ $t("promocode.campaigns-list") }}
          </SDialogTitle>
          <div class="search-container">
            <input
              class="search-input"
              type="search"
              :placeholder="$t('input.promocode-campaigns') + '...'"
              v-model="searchQuery"
            />
            <div class="search-icon-box">
              <SvgIcon
                :iconContent="searchIcon"
                iconAlt="?"
                iconStyle="width: 16px; height: 16px;"
              />
            </div>
          </div>
        </SDialogHeader>

        <div class="campaigns-list">
          <template v-if="hasResults">
            <div
              class="group"
              v-for="key in Object.keys(groupedCards)"
              :key="key"
            >
              <p class="grouping-letter">{{ key }}</p>
              <div class="group-divider"></div>
              <div
                v-for="card in groupedCards[key]"
                :key="card.id"
                class="group-item"
              >
                <PromocodeCampaignExpandedCard
                  :data="card"
                  :toggleApplyPromocode="toggleApplyPromocode"
                />
              </div>
            </div>
          </template>
          <div v-else-if="!isLoading" class="empty-state">
            <p>{{ $t("promocode.no-campaigns-found") }}</p>
          </div>
        </div>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash/debounce";
import { ModalComponents, ModalStatus } from "@/constants/modal";
import { getActivePromocodeCampaigns } from "@/db/promocode-campaign";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import {
  IPromocodeCampaign,
  IPromocodeCardData,
} from "@/types/db/promocode-campaign";
import searchIcon from "@/assets/images/svg/search.svg";
import {
  processPromocode,
  removeCodePayment,
} from "@/utils/vue-helpers/promocode";
import BookingModule from "@/store/modules/booking";
import { LogLevel, LogService } from "@/services/logging";
import ModalModule from "@/store/modules/modal";
import {
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";

export default defineComponent({
  name: ModalComponents.PromocodeCampaigns,
  components: {
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
  },
  setup() {
    const { t } = useI18n();
    const modalVisibility = dialogVisibleForStatus(
      ModalStatus.PromocodeCampaigns
    );

    const promocodeCampaigns = ref<IPromocodeCampaign[]>([]);
    const groupedCards = ref<{
      [key: string]: IPromocodeCardData[];
    }>({});
    const searchQuery = ref("");
    const isLoading = ref(false);
    const error = ref(null);
    const appliedCodes = computed(() => BookingModule.appliedPromocodes);

    async function fetchPromocodeCampaigns(query = "") {
      try {
        isLoading.value = true;
        error.value = null;
        promocodeCampaigns.value = await getActivePromocodeCampaigns({
          pinnedOnly: false,
          searchTerm: query,
        });
        updateCardsList();
      } catch (error) {
        LogService.info(
          `Error when fetching promocode campaigns from DexieDB`,
          {
            params: {
              error: error,
            },
          },
          LogLevel.Error
        );
      } finally {
        isLoading.value = false;
      }
    }

    // we need to show campaigns in alphabetical order, and we need to group them by the first letter of their name
    function groupCardsByName(cards: IPromocodeCardData[]) {
      const grouped: { [key: string]: IPromocodeCardData[] } = {};
      for (const c of cards) {
        const firstLetter = c.name.charAt(0).toUpperCase();
        if (!grouped[firstLetter]) {
          grouped[firstLetter] = [];
        }
        grouped[firstLetter].push(c);
      }
      return grouped;
    }

    async function toggleApplyPromocode(data: IPromocodeCardData) {
      if (data.isApplied) {
        // the promocode is already applied, let's remove it
        removeCodePayment(data.code);
        return;
      }
      ModalModule.setModalProps({ props: data });
      ModalModule.setStatus(ModalStatus.PromocodeCampaignsTickets);
    }

    function updateCardsList() {
      // get all applied codes
      const cards = promocodeCampaigns.value.map((campaign) => {
        const code = campaign.codes[0];
        const isApplied = appliedCodes.value.find(
          (c) => c.promocode.code === code
        );
        return {
          id: campaign.id,
          code,
          name: campaign.name,
          picture: campaign.picture,
          flatValue: campaign.flatValue,
          percentageValue: campaign.percentageValue,
          isApplied: !!isApplied,
        } as IPromocodeCardData;
      });

      // group cards alphabetically
      groupedCards.value = groupCardsByName(cards);
    }

    const debouncedFetchCampaigns = debounce(fetchPromocodeCampaigns, 300);

    watch(searchQuery, (newQuery) => {
      debouncedFetchCampaigns(newQuery);
    });

    fetchPromocodeCampaigns();

    watch(appliedCodes, () => {
      updateCardsList();
    });

    const hasResults = computed(
      () => Object.keys(groupedCards.value).length > 0
    );

    return {
      t,
      modalVisibility,
      searchQuery,
      isLoading,
      searchIcon,
      groupedCards,
      hasResults,
      toggleApplyPromocode,
    };
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;

.promocode-campaigns-modal {
  .search-container {
    position: relative;
  }

  .search-input {
    width: 100%;
    height: 44px;
    padding: 0 52px 0 14px;
    font-size: 14px;
    font-weight: 400;
    color: #333333;
    background-color: #ffffff;
    border: 1px solid #d8d8d8;
    border-radius: 6px;
    transition: border-color 0.15s ease;

    &::placeholder {
      color: #b0b0b0;
    }

    &:focus {
      outline: none;
      border-color: var(--primary-smeetz);
      box-shadow: 0 0 0 3px rgba(var(--primary-smeetz-rgb, 0, 161, 158), 0.12);
    }
  }

  .search-icon-box {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--primary-smeetz);
    border-radius: 6px;
    color: #ffffff;
  }

  .campaigns-list {
    overflow-y: auto;
    padding: 16px 24px 24px;
  }

  .group {
    & + .group {
      margin-top: 20px;
    }
  }

  .grouping-letter {
    font-size: 14px;
    font-weight: 600;
    color: #5e5e63;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 8px;
  }

  .group-divider {
    border-top: 1px solid #ececec;
    margin-bottom: 12px;
  }

  .group-item + .group-item {
    margin-top: 10px;
  }

  .empty-state {
    height: 220px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #8a8a8e;
    font-size: 14px;
  }
}
</style>
